// import Editor from './LexicalDemo/App';
// export default Editor;

import { useMemo, useState } from 'react';
import * as ToolbarPlugin from './plugins/ToolbarPlugin';
import DraggableBlockPlugin from './plugins/DraggableBlockPlugin';
import MediaPlugin from './plugins/MediaPlugin';
import LayoutPlugin from './plugins/LayoutPlugin';
import InlineImagePlugin from './plugins/InlineImagePlugin';
import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { EditorStatePlugin } from './plugins/EditorStatePlugin';
import { VariablesPlugin } from './plugins/VariablesPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { NestedButtonsGeneratorResult } from '../../utils/emails';
import TreeViewPlugin from './plugins/TreeViewPlugin';
import { resolveLexicalEditorProfile } from './resolveLexicalEditorProfile';
import { LexicalEditorProfile } from './profileTypes';
import RawHtmlTableStylePlugin from './nodes/rawHtmlTable/RawHtmlTableStylePlugin';

import './themes/default/styles.css';
import './themes/rawHtml/styles.css';

type EditorProperties =
{
    value: string;
    onChange: (value: string) => void
    generateCustomButtons?: () => NestedButtonsGeneratorResult[]
    variables: string[]
    customSettings: any
    customStyles: any
    charControlHandler: any
    profile?: LexicalEditorProfile
}

const DEBUG_MODE = false;

export default function Editor(properties: EditorProperties)
{
    const { value, variables, onChange, profile } = properties;
    if (properties.customSettings) { console.error('properties.customSettings'); }
    if (properties.customStyles) { console.error('properties.customStyles'); }
    if (properties.charControlHandler) { console.error('properties.charControlHandler'); }
    const resolvedProfile = useMemo(() => resolveLexicalEditorProfile(profile), [profile?.kind]);
    const initialConfig =
    {
        namespace: 'MyEditor',
        theme: resolvedProfile.theme,
        onError(error: Error) { throw error; },
        nodes: resolvedProfile.nodes,
        // html: { import: constructImportMap(), export: constructExportMap() }
        html:
        {
            import: resolvedProfile.importMap,
        }
    };
    const isSmallWidthViewport = false;
    const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | null>(null);
    const ref = (element: HTMLDivElement) => { if (element !== null) { setFloatingAnchorElem(element); }};
    const actions = properties.generateCustomButtons?.();
    const element =
    <LexicalComposer initialConfig={initialConfig}>
        <div
            className={`editor-container ${resolvedProfile.containerClassName ?? ''}`}
            style={resolvedProfile.containerStyle}
        >
            <ToolbarPlugin.Toolbar
                actions={actions}
                debugMode={DEBUG_MODE}
                enableRawHtmlTableActions={resolvedProfile.features?.rawHtmlTableStyle}
            />
            <div className="editor-inner">
                <RichTextPlugin contentEditable={<div ref={ref}><ContentEditable className="editor-input" /></div>} ErrorBoundary={LexicalErrorBoundary} />
                <HistoryPlugin />
                {/* <AutoFocusPlugin /> */}
                <ListPlugin />
                <InlineImagePlugin />
                <TablePlugin />
                {resolvedProfile.features?.rawHtmlTableStyle && <RawHtmlTableStylePlugin />}
                <MediaPlugin />
                <LinkPlugin />
                {floatingAnchorElem && !isSmallWidthViewport && (<DraggableBlockPlugin anchorElem={floatingAnchorElem} />)}
                <VariablesPlugin variables={variables} />
                <EditorStatePlugin initialValue={value} onChange={onChange} resolvedProfile={resolvedProfile} />
                <LayoutPlugin />
                {DEBUG_MODE && <TreeViewPlugin />}
            </div>
        </div>
    </LexicalComposer>;
    return element;
}